import { Ionicons } from '@expo/vector-icons';
import React, { useMemo, useState } from 'react';
import { Pressable, ScrollView, View } from 'react-native';

import { ActivityIndicator, Text, useTheme } from '@/components/ui';
import type { ApplicationApprover, ExteriorApplicationDetail } from '@/services/data-service';
import { REVIEW_ACCENT } from '@/styles/theme';
import { PENDING_OTHERS_STAGES, PENDING_US_STAGES } from '@/utils/application-stage';
import { ExteriorApplicationStatus } from '@/utils/application-status';
import { compareAssociationNames } from '@/utils/association-name';

/**
 * The improvement-request rail (#369) — association → lot, with counts.
 *
 * The inbox rail answers "what KIND of thing is this?"; this one answers "whose is it?".
 * Both are needed and neither substitutes for the other: a property manager works one
 * community at a time, and a flat list of every open request across 77 associations is
 * unusable for that. Requests with no lot go to Unmatched, which is where triage starts.
 *
 * The tree is derived from the ALREADY-LOADED request list rather than a counts endpoint.
 * There are tens of open requests, not thousands, so one fetch beats a second round-trip
 * per node — and it means the counts can never disagree with the list beside them.
 */

const ROW_HEIGHT = 32;
/**
 * The chevron needs a real, wide tap target. `hitSlop` does NOTHING on react-native-web's
 * Pressable, so a near-miss lands on the row behind it and navigates instead of expanding.
 */
const CHEVRON_HIT_WIDTH = 30;

/**
 * Dark amber for the review queue — matches the drawer badge.
 *
 * Not the error red used by Unmatched: work waiting on a reviewer is normal, whereas a
 * request nobody can file at all is genuinely stuck.
 */

/** The status-based views — everything on the rail that is not a place in the tree. */
const STATUS_VIEWS = ['unmatched', 'pending-us', 'pending', 'pending-owner', 'board', 'archived', 'approved'] as const;
type StatusView = typeof STATUS_VIEWS[number];

export interface TreeSelection {
	kind: 'all' | StatusView | 'association' | 'account';
	associationId?: string;
	accountId?: string;
	/** `approved` only: one approver's approvals (#611). */
	decidedBy?: string;
}

/**
 * The URL ⇄ selection mapping, shared by the layout (which lights the rail) and the list
 * (which filters by it) so the two cannot read one URL differently.
 *
 * `?unmatched=1` / `?pendingUs=1` predate `?view=` and are still honoured — they are in
 * people's bookmarks.
 */
export const selectionFromParams = (params: Record<string, string | string[] | undefined>): TreeSelection => {
	const str = (key: string) => (typeof params[key] === 'string' ? params[key] as string : undefined);
	const associationId = str('associationId');
	const accountId = str('accountId');
	if (accountId) return { kind: 'account', associationId, accountId };
	if (associationId) return { kind: 'association', associationId };
	const view = str('view');
	if (view === 'approved') return { kind: 'approved', decidedBy: str('decidedBy') };
	if (view && (STATUS_VIEWS as readonly string[]).includes(view)) return { kind: view as StatusView };
	if (str('unmatched') === '1') return { kind: 'unmatched' };
	if (str('pendingUs') === '1') return { kind: 'pending-us' };
	return { kind: 'all' };
};

export const selectionToParams = (selection: TreeSelection): Record<string, string> => {
	const params: Record<string, string> = {};
	if ((STATUS_VIEWS as readonly string[]).includes(selection.kind)) params.view = selection.kind;
	if (selection.associationId) params.associationId = selection.associationId;
	if (selection.accountId) params.accountId = selection.accountId;
	if (selection.decidedBy) params.decidedBy = selection.decidedBy;
	return params;
};

/**
 * Whether a request belongs in the selected view.
 *
 * Stage, not status: "our move" versus "the homeowner's move" is derived from who spoke last
 * and whether it has been read, which a raw status column cannot express. The caller has
 * already fetched open or archived rows to match, so `all` and the places need no status test.
 */
export const matchesSelection = (
	app: Pick<ExteriorApplicationDetail, 'stage' | 'accountId' | 'associationId' | 'status' | 'decidedBy'>,
	selection: TreeSelection,
): boolean => {
	switch (selection.kind) {
		// The CURRENT status, not the stamp: a reverted approval keeps its `decided_by`.
		case 'approved': return app.status === ExteriorApplicationStatus.Approved
			&& (!selection.decidedBy || app.decidedBy === selection.decidedBy);
		case 'unmatched': return !app.accountId;
		case 'pending-us': return PENDING_US_STAGES.includes(app.stage);
		case 'pending': return PENDING_OTHERS_STAGES.includes(app.stage);
		case 'pending-owner': return app.stage === 'waiting-on-owner';
		case 'board': return app.stage === 'awaiting-board';
		case 'account': return app.accountId === selection.accountId;
		case 'association': return app.associationId === selection.associationId;
		default: return true;
	}
};

/** How many of the loaded requests carry each stage — the fallback when `/counts` has not landed. */
const countStages = (applications: ExteriorApplicationDetail[]): Record<string, number> => {
	const counts: Record<string, number> = {};
	for (const app of applications) if (app.stage) counts[app.stage] = (counts[app.stage] ?? 0) + 1;
	return counts;
};

export interface ApplicationTreeRailProps {
	/** OPEN requests only — the caller fetches with `OPEN_APPLICATIONS_QUERY`. */
	applications: ExteriorApplicationDetail[];
	/** Server per-stage counts (`/counts`), PM-scoped and uncapped. Falls back to the list. */
	byStage?: Record<string, number>;
	selected: TreeSelection;
	onSelect: (selection: TreeSelection) => void;
	loading?: boolean;
	/** Requests sitting at a stage that is our move. */
	pendingUs?: number;
	/** Improvement submissions no request has been opened from yet. */
	unconverted?: number;
	/** Opens the unconverted submissions; absent hides the row. */
	onOpenUnconverted?: () => void;
	/**
	 * Whether the submissions screen is open.
	 *
	 * That view is its own ROUTE rather than a filter of this list, so it cannot be derived
	 * from `selected` — without it the rail leaves "All requests" lit while showing
	 * something else entirely.
	 */
	submissionsActive?: boolean;
	/** Opens the combined needs-review screen. */
	onOpenNeedsReview?: () => void;
	/** Whether that combined screen is open — also its own route. */
	needsReviewActive?: boolean;
	/** Opens the portfolio Exterior Improvements report (#644); absent hides the row. */
	onOpenReport?: () => void;
	/** Approved requests per approver (#611); absent hides the group. */
	approvers?: ApplicationApprover[];
}

interface AccountNode {
	accountId: string;
	label: string;
	count: number;
}

interface AssociationNode {
	associationId: string;
	name: string;
	/** The PM who owns it — `associations.assigned_to`, carried through on every row. */
	managerId: string | null;
	managerName: string | null;
	count: number;
	accounts: AccountNode[];
}

const Row: React.FC<{
	icon: keyof typeof Ionicons.glyphMap;
	name: string;
	/**
	 * Always rendered when given — ZERO included (#581). "0 waiting" is a fact; an absent badge
	 * reads as "not loaded" or "not counted". Omit it only for rows that are deliberately
	 * uncounted (Archived: a number that only grows is not a to-do).
	 */
	count?: number;
	depth?: number;
	selected: boolean;
	color?: string;
	onPress: () => void;
	expandable?: boolean;
	expanded?: boolean;
	onToggle?: () => void;
	testID: string;
}> = ({ icon, name, count, depth = 0, selected, color, onPress, expandable, expanded, onToggle, testID }) => {
	const theme = useTheme();
	const [hovered, setHovered] = useState(false);
	return (
		<View style={{ flexDirection: 'row', alignItems: 'stretch' }}>
			{expandable ? (
				<Pressable
					onPress={onToggle}
					testID={`${testID}-toggle`}
					style={{
						width: CHEVRON_HIT_WIDTH,
						marginLeft: depth * 14,
						alignItems: 'center',
						justifyContent: 'center',
					}}
				>
					<Ionicons
						name={expanded ? 'chevron-down' : 'chevron-forward'}
						size={14}
						color={theme.colors.onSurfaceVariant}
					/>
				</Pressable>
			) : (
				<View style={{ width: expandable === false ? CHEVRON_HIT_WIDTH : 0, marginLeft: depth * 14 }} />
			)}
			<Pressable
				testID={testID}
				onPress={onPress}
				onHoverIn={() => setHovered(true)}
				onHoverOut={() => setHovered(false)}
				style={{
					flex: 1, minWidth: 0,
					flexDirection: 'row', alignItems: 'center',
					minHeight: ROW_HEIGHT,
					paddingVertical: 5, paddingHorizontal: 8,
					borderRadius: 8, marginBottom: 1,
					backgroundColor: selected
						? theme.colors.primaryContainer
						: hovered ? theme.colors.surfaceVariant : 'transparent',
				}}
			>
				<Ionicons name={icon} size={15} color={color ?? theme.colors.onSurfaceVariant} style={{ marginRight: 8 }} />
				<Text
					numberOfLines={1}
					style={{
						flex: 1, minWidth: 0, fontSize: 13,
						fontWeight: selected ? '700' : '400',
						color: selected ? theme.colors.onSurface : theme.colors.onSurfaceVariant,
					}}
				>
					{name}
				</Text>
				{count != null ? (
					<View
						testID={`${testID}-badge`}
						style={{
							minWidth: 20, height: 18, paddingHorizontal: 6, borderRadius: 9,
							alignItems: 'center', justifyContent: 'center',
							// Zero stays grey even when selected — highlighting "nothing" would
							// read as an alert.
							backgroundColor: selected && count > 0 ? theme.colors.primary : theme.colors.surfaceVariant,
						}}
					>
						<Text
							style={{
								fontSize: 11, fontWeight: '700',
								color: selected && count > 0 ? theme.colors.onPrimary : theme.colors.onSurfaceVariant,
							}}
						>
							{count}
						</Text>
					</View>
				) : null}
			</Pressable>
		</View>
	);
};

export const ApplicationTreeRail: React.FC<ApplicationTreeRailProps> = ({
	applications, byStage, selected, onSelect, loading = false, pendingUs, unconverted, onOpenUnconverted,
	submissionsActive = false, onOpenNeedsReview, needsReviewActive = false, onOpenReport, approvers,
}) => {
	const theme = useTheme();
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const loadedStages = useMemo(() => countStages(applications), [applications]);
	const stageCount = (stage: string) => byStage?.[stage] ?? loadedStages[stage] ?? 0;
	const needsInfo = stageCount('waiting-on-owner');
	const withBoard = stageCount('awaiting-board');
	// A route of its own lights its row, so the status rows must stand down while one is open.
	const onRoute = submissionsActive || needsReviewActive;
	const isSelected = (kind: TreeSelection['kind']) => selected.kind === kind && !onRoute;
	const divider = (
		<View style={{ height: 1, backgroundColor: theme.colors.outlineVariant ?? theme.colors.outline, marginVertical: 8 }} />
	);

	const { unmatched, tree } = useMemo(() => {
		const byAssociation = new Map<string, AssociationNode>();
		let unmatchedCount = 0;
		for (const app of applications) {
			if (!app.accountId) unmatchedCount++;
			if (!app.associationId) continue;
			let node = byAssociation.get(app.associationId);
			if (!node) {
				node = {
					associationId: app.associationId,
					name: app.associationName || 'Unnamed',
					managerId: app.managerId ?? null,
					managerName: app.managerName ?? null,
					count: 0,
					accounts: [],
				};
				byAssociation.set(app.associationId, node);
			}
			node.count++;
			if (!app.accountId) continue;
			const label = [app.accountNumber, app.accountOwnerName].filter(Boolean).join(' - ')
				|| app.propertyAddress || 'Lot';
			const account = node.accounts.find((a) => a.accountId === app.accountId);
			if (account) account.count++;
			else node.accounts.push({ accountId: app.accountId, label, count: 1 });
		}
		const nodes = [...byAssociation.values()].sort((a, b) => compareAssociationNames(a.name, b.name));
		for (const node of nodes) node.accounts.sort((a, b) => a.label.localeCompare(b.label));
		return { unmatched: unmatchedCount, tree: nodes };
	}, [applications]);

	/**
	 * Group the associations under their property manager — support and admin only.
	 *
	 * Inferred from the DATA rather than the role, exactly like the inbox rail: if every
	 * association on this rail has the same manager, that manager is you, and filing your
	 * own associations under your own name is a wasted level of nesting.
	 */
	const managerTree = useMemo(() => {
		const byManager = new Map<string, { managerId: string; name: string; nodes: AssociationNode[] }>();
		for (const node of tree) {
			const key = node.managerId ?? 'unassigned';
			const entry = byManager.get(key)
				?? { managerId: key, name: node.managerName ?? 'Unassigned', nodes: [] };
			entry.nodes.push(node);
			byManager.set(key, entry);
		}
		return [...byManager.values()].sort((a, b) => a.name.localeCompare(b.name));
	}, [tree]);

	const groupByManager = managerTree.length > 1;

	const toggle = (id: string) => setExpanded((prev) => {
		const next = new Set(prev);
		if (next.has(id)) next.delete(id); else next.add(id);
		return next;
	});

	// Opens BOTH levels — expanding only the managers leaves support one click short of
	// what they came for, which is not what "expand all" promises.
	const setAll = (open: boolean) => setExpanded(open
		? new Set([
			...tree.map((n) => n.associationId),
			...managerTree.map((m) => `mgr:${m.managerId}`),
		])
		: new Set());

	/** Shared by both shapes, so a manager branch renders identical rows to a flat rail. */
	const renderAssociation = (node: AssociationNode, depth: 0 | 1) => (
		<React.Fragment key={node.associationId}>
			<Row
				icon="business-outline"
				name={node.name}
				count={node.count}
				depth={depth}
				selected={selected.kind === 'association' && selected.associationId === node.associationId}
				onPress={() => onSelect({ kind: 'association', associationId: node.associationId })}
				expandable={node.accounts.length > 0}
				expanded={expanded.has(node.associationId)}
				onToggle={() => toggle(node.associationId)}
				testID={`tree-association-${node.associationId}`}
			/>
			{expanded.has(node.associationId) ? node.accounts.map((account) => (
				<Row
					key={account.accountId}
					icon="person-outline"
					name={account.label}
					count={account.count}
					depth={depth + 1}
					expandable={false}
					selected={selected.kind === 'account' && selected.accountId === account.accountId}
					onPress={() => onSelect({
						kind: 'account',
						associationId: node.associationId,
						accountId: account.accountId,
					})}
					testID={`tree-account-${account.accountId}`}
				/>
			)) : null}
		</React.Fragment>
	);

	return (
		<ScrollView style={{ flex: 1 }} contentContainerStyle={{ paddingVertical: 8, paddingHorizontal: 6 }} testID="application-tree-rail">
			<View style={{ flexDirection: 'row', justifyContent: 'flex-end', gap: 12, paddingHorizontal: 6, paddingBottom: 6 }}>
				<Pressable onPress={() => setAll(true)} testID="btn-expand-all" style={{ paddingVertical: 4 }}>
					<Text style={{ fontSize: 12, color: theme.colors.primary, fontWeight: '600' }}>Expand all</Text>
				</Pressable>
				<Pressable onPress={() => setAll(false)} testID="btn-collapse-all" style={{ paddingVertical: 4 }}>
					<Text style={{ fontSize: 12, color: theme.colors.primary, fontWeight: '600' }}>Collapse all</Text>
				</Pressable>
			</View>

			{/* The report is its own route (the layout drops this rail there), so it never lights up. */}
			{onOpenReport ? (
				<Row
					icon="document-text-outline"
					name="Report"
					selected={false}
					onPress={onOpenReport}
					testID="tree-report"
				/>
			) : null}

			{/*
				"All Applications" is every OPEN request (#581) — decided ones live under Archived,
				so the top of the rail is the whole of the live workload and nothing else.
			*/}
			<Row
				icon="albums-outline"
				name="All Applications"
				count={applications.length}
				// Both review screens are separate ROUTES with no selection of their own, so
				// "all" stays the nominal selection while they are open — and the rail would
				// claim to be showing every request while showing a queue of unfiled work.
				selected={isSelected('all')}
				onPress={() => onSelect({ kind: 'all' })}
				testID="tree-all"
			/>
			{divider}

			{/*
				A submission that never became a request is invisible to the tree below — which
				is how a queue of real ones sat here reading "No open requests" and the team
				concluded there was nothing to do. Every row carries a badge even at zero:
				"0 waiting" is a fact, an absent badge is an assumption.
			*/}
			<View testID="applications-needs-human">
				{/*
					Selectable in its own right, with the two kinds of waiting work as folders
					beneath it. The parent is the one someone clicks every morning; the folders
					are for when they want to work just one kind.
				*/}
				<Row
					icon="alert-circle"
					name="Needs Review"
					count={(unconverted ?? 0) + (pendingUs ?? 0)}
					color={REVIEW_ACCENT}
					selected={needsReviewActive}
					onPress={() => onOpenNeedsReview?.()}
					testID="tree-needs-review"
				/>
				{onOpenUnconverted ? (
					<Row
						icon="mail-unread-outline"
						name="New submissions"
						count={unconverted ?? 0}
						color={unconverted ? REVIEW_ACCENT : undefined}
						selected={submissionsActive}
						onPress={onOpenUnconverted}
						depth={1}
						testID="tree-unconverted"
					/>
				) : null}
				<Row
					icon="hourglass-outline"
					name="Pending our review"
					count={pendingUs ?? 0}
					color={pendingUs ? REVIEW_ACCENT : undefined}
					selected={isSelected('pending-us')}
					onPress={() => onSelect({ kind: 'pending-us' })}
					depth={1}
					testID="tree-pending-us"
				/>
			</View>
			{divider}

			{/*
				Open, but the ball is in someone else's court. Kept apart from Needs Review so that
				group still means "ours", and not amber — waiting on the homeowner or the board is
				not something this team can clear.
			*/}
			<Row
				icon="time-outline"
				name="Pending"
				count={needsInfo + withBoard}
				selected={isSelected('pending')}
				onPress={() => onSelect({ kind: 'pending' })}
				testID="tree-pending"
			/>
			<Row
				icon="document-text-outline"
				name="Pending Owner"
				count={needsInfo}
				selected={isSelected('pending-owner')}
				onPress={() => onSelect({ kind: 'pending-owner' })}
				depth={1}
				testID="tree-pending-owner"
			/>
			<Row
				icon="people-outline"
				name="Pending Board Review"
				count={withBoard}
				selected={isSelected('board')}
				onPress={() => onSelect({ kind: 'board' })}
				depth={1}
				testID="tree-board"
			/>
			{divider}

			{/* The error colour: a request nobody can file is the one thing on this rail that
			    is actively stuck. */}
			<Row
				icon="alert-circle-outline"
				name="Unmatched"
				count={unmatched}
				color={theme.colors.error}
				selected={isSelected('unmatched')}
				onPress={() => onSelect({ kind: 'unmatched' })}
				testID="tree-unmatched"
			/>
			{/* No badge: the only uncounted row. A total that only ever grows is not work. */}
			<Row
				icon="archive-outline"
				name="Archived"
				selected={isSelected('archived')}
				onPress={() => onSelect({ kind: 'archived' })}
				testID="tree-archived"
			/>
			{/*
				Approved, by who approved it (#611). An approval granted too early could not be
				found anywhere; this is where "what has this person approved?" is answered. The
				counts are informational — the server's, uncapped — not a to-do, so the parent
				carries none, the same rule as Archived.
			*/}
			{approvers ? (
				<>
					<Row
						icon="checkmark-done-outline"
						name="Approved"
						selected={selected.kind === 'approved' && !selected.decidedBy && !onRoute}
						onPress={() => onSelect({ kind: 'approved' })}
						testID="tree-approved"
					/>
					{approvers.map((approver) => (
						<Row
							key={approver.decidedBy}
							icon="person-outline"
							name={approver.name ?? 'Former user'}
							count={approver.count}
							depth={1}
							selected={selected.kind === 'approved' && selected.decidedBy === approver.decidedBy && !onRoute}
							onPress={() => onSelect({ kind: 'approved', decidedBy: approver.decidedBy })}
							testID={`tree-approver-${approver.decidedBy}`}
						/>
					))}
				</>
			) : null}
			{divider}

			{loading && applications.length === 0 ? (
				<View style={{ paddingVertical: 24, alignItems: 'center' }}><ActivityIndicator size="small" /></View>
			) : tree.length === 0 ? (
				<Text style={{ padding: 12, color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }}>No open requests.</Text>
			) : groupByManager
				? managerTree.map((manager) => (
					<React.Fragment key={manager.managerId}>
						<Row
							icon="person-circle-outline"
							name={manager.name}
							count={manager.nodes.reduce((sum, n) => sum + n.count, 0)}
							// A manager is a HEADING, not a filter — there is no
							// ?managerId= on this screen, and inventing one would open a
							// list no property manager could reproduce. Pressing the label
							// therefore toggles, like the chevron.
							selected={false}
							onPress={() => toggle(`mgr:${manager.managerId}`)}
							expandable
							expanded={expanded.has(`mgr:${manager.managerId}`)}
							onToggle={() => toggle(`mgr:${manager.managerId}`)}
							testID={`tree-manager-${manager.managerId}`}
						/>
						{expanded.has(`mgr:${manager.managerId}`)
							? manager.nodes.map((node) => renderAssociation(node, 1))
							: null}
					</React.Fragment>
				))
				: tree.map((node) => renderAssociation(node, 0))}
		</ScrollView>
	);
};

export default ApplicationTreeRail;
